import { IItemModifierGroupsResponseData } from 'src/api/getModifierGroups';
import {
  IItemModifierGroupResponseDataModifiers,
  ItemModifierGroupsResponseBody,
} from 'src/api/getModifierGroups/types';
import { OptionGroup } from 'src/api/getOptionGroups/types';
import { TOptionGroupRecord, TOptionGroupRow } from './types';

export const mapFieldValueToPrice = ([key, value]: [string, TOptionGroupRecord]) => {
  const price_id = value.price_id ?? null;
  const inherit_price = value.inherit_price ?? false;
  const value_with_tax = value.value_with_tax ? Number.parseFloat(value.value_with_tax) : null;

  return {
    price_id: price_id,
    value_with_tax: inherit_price ? null : value_with_tax,
    modifier_id: key,
    price_band_id: null,
    extension_instance_id: null,
  };
};

type TModifierFormDefaultInput = Pick<
  IItemModifierGroupResponseDataModifiers,
  'modifier_id' | 'price_id' | 'value_with_tax' | 'inherit_price'
>;

export const generateModifierFormDefaults = (modifiers: TModifierFormDefaultInput[]) => {
  const entries = modifiers.map((modifier) => {
    const { modifier_id, price_id, value_with_tax, inherit_price } = modifier;
    return [
      modifier_id,
      {
        price_id,
        value_with_tax: value_with_tax === null ? '' : String(value_with_tax),
        inherit_price,
      },
    ];
  });

  return Object.fromEntries(entries);
};

export const flattenPagesToModifiers = (pages: ItemModifierGroupsResponseBody[] = []) => {
  const flattenedPages = pages?.flatMap(({ data }) => data) ?? [];

  return flattenedPages.flatMap((group) => group.modifier_groups).flatMap((modifier_group) => modifier_group.modifiers);
};

export const mapModifierGroupToTableRow = (item: IItemModifierGroupsResponseData): TOptionGroupRow => {
  return {
    item_id: item.item_id,
    name: item.name,
    type: item.type,
    ranges: item.ranges,
    modifiers: item.modifier_groups.map((nested) => ({
      name: nested.name,
      publish_channels: nested.external_systems,
      modifiers: nested.modifiers.map((modifier) => ({
        name: modifier.name,
        price_id: modifier.price_id,
        modifier_id: modifier.modifier_id,
        inherit_price: modifier.inherit_price,
        ranges: modifier.ranges,
        value_with_tax: String(modifier.value_with_tax),
      })),
    })),
  };
};

export const mapStandaloneOptionGroupToTableRow = (group: OptionGroup): TOptionGroupRow => {
  const externalSystems = group.external_systems ?? [];
  const options = group.options ?? [];

  return {
    option_group_id: group.option_group_id,
    name: group.name,
    type: group.type,
    ranges: group.ranges,
    publish_channels: externalSystems.map((es) => ({ id: es.external_system_id, name: es.name })),
    modifiers: options.map((option) => ({
      name: option.name,
      price_id: option.price_id,
      modifier_id: option.option_id,
      inherit_price: option.inherit_price,
      ranges: option.ranges,
      value_with_tax: option.value_with_tax === null ? '' : String(option.value_with_tax),
    })),
  };
};

export const flattenOptionGroupsToModifiers = (groups: OptionGroup[] = []): TModifierFormDefaultInput[] =>
  groups
    .flatMap((group) => group.options ?? [])
    .map((option) => ({
      modifier_id: option.option_id,
      price_id: option.price_id,
      value_with_tax: option.value_with_tax,
      inherit_price: option.inherit_price,
    }));

export enum StandaloneDepth {
  Group = 0,
  Option = 1,
}
